import { useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { CheckCircle, ShieldCheck } from '@phosphor-icons/react';
import PrimaryButton from '../../../components/atoms/Button';
import ModalFrame from '../../../components/molecules/ModalFrame';
import {
	Drawer,
	DrawerContent,
	DrawerTitle,
} from '@/components/ui/drawer';
import SATCredentialsForm from './SATCredentialsForm';
import PasswordRecoveryContent from './PasswordRecoveryContent';
import { useCredentialsForm } from './orchestration/useCredentialsForm';
import { useLinkingTracking } from './orchestration/useLinkingTracking';
import type { LinkingFlowSubmitData } from './orchestration/useSATLinking';

const EASE = [0.22, 1, 0.36, 1] as const;
const POP = [0.18, 0.89, 0.32, 1.28] as const;

type LinkingCredentialsLightProps = {
	onSubmit: (data: LinkingFlowSubmitData) => void;
	isLoading: boolean;
	// Chrome propio del consumidor (ej. back + Saltar del onboarding).
	header?: ReactNode;
};

// Variante "light" del step de credenciales: el mismo formulario y orquestación
// del flujo de vinculación, presentado como pantalla minimalista (badge + copy
// centrado), sin la tarjeta pesada con FAQs. Comparte useCredentialsForm con
// LinkingFlowStep2, así que NO duplica el prellenado del RFC.
const LinkingCredentialsLight = ({
	onSubmit,
	isLoading,
	header,
}: LinkingCredentialsLightProps) => {
	const [showRecovery, setShowRecovery] = useState(false);
	const [selectedSite, setSelectedSite] = useState<string | null>(null);
	const { trackAction } = useLinkingTracking();

	const {
		control,
		handleSubmit,
		rfcPrefill,
		initialDrawerCurp,
		initialDrawerFullName,
		handleRfcGenerated,
	} = useCredentialsForm({
		onRfcPrefilled: (source) => trackAction('link', 'rfc_prefilled', { source }),
		onRfcPrefillMissed: (missed) =>
			trackAction('link', 'rfc_prefill_missed', missed),
	});

	const onConnect = ({
		rfc,
		password,
		rfcPrefillTracking,
	}: Pick<LinkingFlowSubmitData, 'rfc' | 'password' | 'rfcPrefillTracking'>) => {
		onSubmit({
			rfc: rfc.toUpperCase(),
			password,
			rfcPrefillTracking,
		} as LinkingFlowSubmitData);
	};

	return (
		<div className='flex flex-1 flex-col'>
			{header}

			<div className='flex flex-1 items-center justify-center px-5 py-8 sm:px-6 sm:py-12'>
				<motion.form
					onSubmit={handleSubmit(onConnect)}
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.25, ease: EASE }}
					className='flex w-full max-w-md flex-col gap-6 sm:gap-8'>
					<div className='flex flex-col items-center gap-3 text-center sm:gap-4'>
						<motion.span
							initial={{ scale: 0.6, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							transition={{ duration: 0.35, ease: POP, delay: 0.1 }}
							className='bg-h-primary-90/50 text-h-primary flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold sm:gap-2 sm:px-4 sm:py-2 sm:text-base'>
							Encriptado
							<ShieldCheck weight='fill' className='size-[18px] sm:size-5' />
						</motion.span>
						<div className='flex flex-col gap-1.5 sm:gap-2'>
							<h1 className='text-h-text-heading text-xl leading-snug font-bold tracking-tight sm:text-3xl sm:leading-tight'>
								Conecta tu cuenta del SAT
							</h1>
							<p className='text-h-text-medium text-sm leading-relaxed sm:text-base'>
								Conéctala para tener tus facturas, declaraciones presentadas y
								toda tu información fiscal siempre a la mano.
							</p>
						</div>
					</div>

					<SATCredentialsForm
						// SATCredentialsForm types `control` as Control<any>; el shape
						// {rfc,password,...} dispara el tipo contravariante de RHF.
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						control={control as any}
						passwordVisibility='always'
						rfcPrefill={rfcPrefill}
						initialCurp={initialDrawerCurp}
						initialFullName={initialDrawerFullName}
						onRfcGenerated={handleRfcGenerated}
						onPasswordRecoveryClick={() => {
							trackAction('link', 'password_help_opened', {
								error_code: null,
							});
							setShowRecovery(true);
						}}
					/>

					<PrimaryButton
						type='submit'
						label='Conectar cuenta'
						loading={isLoading}
						icon={
							<CheckCircle weight='fill' size={20} className='opacity-50' />
						}
					/>
				</motion.form>
			</div>

			<Drawer open={showRecovery} onOpenChange={setShowRecovery}>
				<DrawerContent className='inset-x-auto left-1/2 w-full max-w-lg -translate-x-1/2'>
					<DrawerTitle className='sr-only'>
						¿Cómo recupero mi contraseña del SAT?
					</DrawerTitle>
					<div className='flex flex-col gap-1 px-5 pt-2 pb-3 pr-14'>
						<h2 className='text-h-text-heading text-xl font-bold'>
							¿Cómo recupero mi contraseña del SAT?
						</h2>
					</div>
					<div className='flex flex-col gap-4 px-5 pt-2 pb-6'>
						<PasswordRecoveryContent onOpenSite={setSelectedSite} />
					</div>
				</DrawerContent>
			</Drawer>

			{selectedSite && (
				<ModalFrame
					open
					onClose={() => setSelectedSite(null)}
					src={selectedSite}
				/>
			)}
		</div>
	);
};

export default LinkingCredentialsLight;
